import { createFileRoute, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useRestockAuditLogs } from '@/api/audit-logs'
import { useIdempotencyKey } from '@/api/idempotency'
import { poolLabel } from '@/api/pools'
import { useRestock, useUpdateRestockNote, useVoidRestock } from '@/api/restocks'
import type { Restock, RestockLine, RestockMovement } from '@/api/restocks'
import { useWorkspace } from '@/api/workspace'
import { correctionErrorMessage } from '@/components/corrections/error-message'
import { NoteCard } from '@/components/corrections/note-card'
import type { NoteSave } from '@/components/corrections/note-card'
import { VoidDialog } from '@/components/corrections/void-dialog'
import { VoidedBadge, VoidedNotice } from '@/components/corrections/voided'
import { voidedClass } from '@/components/corrections/voided-record'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatCostPerUnit, formatMoney, formatQty } from '@/components/materials/pool-format'
import { formatBusinessDate } from '@/components/opening-balances/format'
import { RecordHistory } from '@/components/record-history/record-history'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

// One Restock (UI-SCREENS M-06, #66): its lines, the Expense it created (read-only), the
// movements it posted, who recorded it and on which business date. A posted record is corrected
// by voiding it (Owner, with a reason); only its note stays editable (#68). The Owner also sees
// its History (#69).
export const Route = createFileRoute('/w/$slug/materials/restocks/$restockId/')({
  component: RestockDetail,
  staticData: { breadcrumb: 'restocks.detail.title' },
})

function RestockDetail() {
  const { t } = useTranslation()
  const { slug, restockId } = Route.useParams()
  const id = Number(restockId)
  const restock = useRestock(slug, id)
  const isOwner = useWorkspace(slug).data?.role === 'owner'
  const history = useRestockAuditLogs(slug, id, { enabled: isOwner })
  const voidRestock = useVoidRestock()
  const updateNote = useUpdateRestockNote()
  const idempotency = useIdempotencyKey()

  if (restock.isPending) return <LoadingSkeleton variant="page" />
  if (restock.isError || !restock.data) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {t('errors.generic')}
      </p>
    )
  }

  const document = restock.data
  const voided = Boolean(document.voided_at)

  // One Idempotency-Key per submission; the void is a transition, so it carries one.
  const onVoid = (reason: string, done: () => void) => {
    const body = { void: { reason } }
    voidRestock.mutate(
      { params: { path: { slug, restock_id: id }, header: idempotency.headerFor(body) }, body },
      { onSuccess: done, onSettled: (_data, error) => idempotency.settle(error) },
    )
  }

  const onSaveNote = (note: string | null, save: NoteSave) => {
    updateNote.mutate(
      { params: { path: { slug, id } }, body: { restock: { note, lock_version: save.lockVersion } } },
      { onSuccess: save.done, onSettled: (_data, error) => save.settle(error) },
    )
  }

  return (
    <section className={`flex max-w-2xl flex-col gap-4 ${voidedClass(voided)}`}>
      <Link to="/w/$slug/materials/restocks" params={{ slug }} className="text-sm underline-offset-4 hover:underline">
        {t('restocks.back')}
      </Link>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-xl font-semibold">{formatBusinessDate(document.occurred_on)}</h1>
        <div className="flex items-center gap-3">
          <VoidedBadge record={document} />
          {document.can.void ? (
            <VoidDialog
              onVoid={onVoid}
              pending={voidRestock.isPending}
              errorMessage={voidRestock.isError ? correctionErrorMessage(t, voidRestock.error) : ''}
              onReset={voidRestock.reset}
            />
          ) : null}
        </div>
      </div>

      <Card>
        <CardContent className="flex flex-col gap-3 py-4">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-3">
            <Figure label={t('restocks.columns.vendor')} value={restock.data.vendor ?? t('restocks.noVendor')} />
            <Figure label={t('restocks.columns.total')} value={`Rp ${formatMoney(restock.data.total)}`} />
            <Figure
              label={t('restocks.columns.createdBy')}
              value={restock.data.created_by ? (restock.data.created_by.name ?? restock.data.created_by.email) : '—'}
            />
          </dl>
          <VoidedNotice record={document} />
        </CardContent>
      </Card>

      <NoteCard
        note={document.note}
        lockVersion={document.lock_version}
        canEdit={document.can.update_note}
        pending={updateNote.isPending}
        errorMessage={updateNote.isError ? correctionErrorMessage(t, updateNote.error) : ''}
        onSave={onSaveNote}
        onReset={updateNote.reset}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('restocks.form.lines')}</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="flex flex-col divide-y">
            {restock.data.lines.map((line) => (
              <li key={line.id} className="py-3 first:pt-0">
                <LineRow line={line} />
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <ExpenseCard expense={restock.data.expense} />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('restocks.detail.movements')}</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="flex flex-col divide-y">
            {restock.data.movements.map((movement) => (
              <li key={movement.id} className="py-3 first:pt-0">
                <MovementRow movement={movement} />
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {isOwner ? <RecordHistory entries={history.data} isPending={history.isPending} isError={history.isError} /> : null}
    </section>
  )
}

// The one Expense this Restock created. Read-only: it changes only by voiding the Restock
// (domain-rules Corrections). The reserved category is labelled here, not stored in Indonesian.
function ExpenseCard({ expense }: { expense: Restock['expense'] }) {
  const { t } = useTranslation()

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('restocks.detail.expense')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <Figure label={t('restocks.columns.total')} value={`Rp ${formatMoney(expense.amount)}`} />
          <Figure
            label={t('restocks.detail.category')}
            value={expense.category.system ? t('restocks.detail.expenseCategory') : expense.category.name}
          />
        </dl>
        {expense.voided_at ? <VoidedBadge record={expense} /> : null}
        <p className="text-sm text-muted-foreground">{t('restocks.detail.expenseReadOnly')}</p>
      </CardContent>
    </Card>
  )
}

function LineRow({ line }: { line: RestockLine }) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-1">
      <span className="font-medium">{poolLabel(line.pool)}</span>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-3">
        <Figure label={t('restocks.form.qty')} value={`${formatQty(line.qty)} ${line.pool.unit}`} />
        <Figure label={t('restocks.columns.total')} value={`Rp ${formatMoney(line.amount)}`} />
        <Figure label={t('restocks.form.costPerUnit')} value={`Rp ${formatCostPerUnit(line.cost_per_unit)}`} />
      </dl>
    </div>
  )
}

// One movement per Pool: lines that share a Pool were summed before posting (decided Q-5), so
// the movement's numbers can differ from any single line's. A void adds the exact negation of
// each row, linked to it by `reverses_movement_id` (UI-SCREENS §2).
function MovementRow({ movement }: { movement: RestockMovement }) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{poolLabel(movement.pool)}</span>
        {movement.reverses_movement_id ? (
          <Badge variant="outline">{t('corrections.reversalOf', { id: movement.reverses_movement_id })}</Badge>
        ) : null}
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <Figure label={t('materials.movements.columns.qty')} value={signedQty(movement.qty_delta)} />
        <Figure label={t('materials.movements.columns.value')} value={signedMoney(movement.value_delta)} />
      </dl>
    </div>
  )
}

const signedQty = (value: number) => `${value > 0 ? '+' : value < 0 ? '−' : ''}${formatQty(Math.abs(value))}`
const signedMoney = (value: number) =>
  `${value > 0 ? '+' : value < 0 ? '−' : ''}Rp ${formatMoney(Math.abs(value))}`

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{value}</dd>
    </div>
  )
}
